import { UiComponentModel } from '../models/dashboard.model';

export interface WidgetPlacement {
  readonly x?: number;
  readonly y?: number;
}

export const getRightSidePlacement = (
  existingComponents: readonly UiComponentModel[],
  newComponent: Pick<UiComponentModel, 'cols' | 'rows'>,
  gridColumns: number
): WidgetPlacement => {
  if (!existingComponents.length) {
    return { x: 0, y: 0 };
  }

  const newRows = Math.max(newComponent.rows ?? 1, 1);
  const maxBottomEdge = existingComponents.reduce((maxBottom, component) => {
    const y = component.y ?? 0;
    return Math.max(maxBottom, y + component.rows);
  }, 0);

  // Prefer top-most rows first. For each candidate y, only consider components that
  // overlap that vertical band; this prevents a wide lower widget from affecting
  // placement of a component that fits in a top-right gap.
  for (let candidateY = 0; candidateY <= maxBottomEdge; candidateY += 1) {
    const maxRightEdge = existingComponents.reduce((maxEdge, component) => {
      const componentY = component.y ?? 0;
      const componentBottom = componentY + component.rows;
      const candidateBottom = candidateY + newRows;
      const overlapsVertically = componentY < candidateBottom && candidateY < componentBottom;

      if (!overlapsVertically) {
        return maxEdge;
      }

      const x = component.x ?? 0;
      return Math.max(maxEdge, x + component.cols);
    }, 0);

    if (maxRightEdge + newComponent.cols <= gridColumns) {
      return { x: maxRightEdge, y: candidateY };
    }
  }

  // If we cannot append to the right, place the widget below current content.
  return { x: 0, y: maxBottomEdge };
};
